import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { RoleGate } from '@/components/RoleGate';
import { UnsavedChangesBar } from '@/components/ui/UnsavedChangesBar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateWorkspace } from '@/features/workspaces/hooks/useUpdateWorkspace';
import { type CreateWorkspaceInput, createWorkspaceSchema } from '@/lib/schemas';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';

export const Route = createFileRoute('/_app/$wsSlug/settings/general')({
  component: GeneralSettingsPage,
});

function GeneralSettingsPage() {
  const { wsSlug } = Route.useParams();
  const { data: workspace, isLoading } = useQuery(getWorkspaceBySlugQuery(wsSlug));

  const update = useUpdateWorkspace(workspace?.id ?? '', wsSlug);

  const form = useForm<CreateWorkspaceInput>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: '', slug: '' },
  });

  // Pre-populate form once workspace loads
  useEffect(() => {
    if (!workspace) return;
    form.reset({ name: workspace.name, slug: workspace.slug });
  }, [workspace, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!workspace) return;
    await update.mutateAsync(values);
    // Limpia el estado dirty de inmediato (la barra de cambios desaparece sin
    // esperar al refetch del workspace).
    form.reset(values);
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-quiet text-sm">Cargando...</div>
    );
  }

  if (!workspace) {
    return <div className="text-sm text-critical py-4">Workspace no encontrado.</div>;
  }

  return (
    <RoleGate wsSlug={wsSlug}>
      <div className="max-w-lg mb-4">
        <h2 className="text-base font-semibold text-ink mb-1">Informacion general</h2>
        <p className="text-sm text-quiet mb-6">
          Configuración general del workspace: nombre, slug y logo. Cambios afectan a todos los
          miembros.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-name">Nombre del workspace</Label>
            <Input id="ws-name" placeholder="Mi Empresa" {...form.register('name')} />
            {form.formState.errors.name && (
              <span className="text-xs text-critical">{form.formState.errors.name.message}</span>
            )}
          </div>

          {/* Slug */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-slug">Slug (URL)</Label>
            <div className="flex items-center rounded-md border border-border overflow-hidden focus-within:border-accent transition-colors">
              <span className="px-3 h-9 inline-flex items-center text-sm text-quiet bg-surface-elev border-r border-border shrink-0">
                meidei.app/
              </span>
              <input
                id="ws-slug"
                {...form.register('slug')}
                className="flex-1 h-9 px-3 text-sm bg-surface outline-none text-ink"
                placeholder="mi-empresa"
              />
            </div>
            {form.formState.errors.slug && (
              <span className="text-xs text-critical">{form.formState.errors.slug.message}</span>
            )}
            <p className="text-xs text-mute">
              Solo minusculas, numeros y guiones. Cambiar el slug cambiara todas las URLs.
            </p>
          </div>
        </form>
      </div>

      {/* Barra sticky full-bleed: compensa el padding (p-6) del layout de
          settings para pegarse al fondo del area visible mientras haya
          cambios. */}
      <UnsavedChangesBar
        className="-mx-6 -mb-6"
        isDirty={form.formState.isDirty}
        isSaving={update.isPending}
        onSave={() => void onSubmit()}
        onDiscard={() => form.reset()}
      />
    </RoleGate>
  );
}
